import React, { useState, useEffect, useRef } from 'react'
import { Outlet, Link, useLocation } from 'react-router-dom'
import { useUser, useClerk } from '@clerk/react'
import { useAuthStore } from '../../hooks/useAuthStore'

/* ─────────────────────────────────────────────────────────────
   Design tokens — stitch dashboard deep purple theme
───────────────────────────────────────────────────────────── */
const C = {
  purple:      '#6200ee',
  purpleHover: '#5300cd',
  purpleBg:    'rgba(98,0,238,0.15)',  // active item bg
  purpleDark:  '#22005d',
  sidebarBg:   '#22005d',
  white:       '#ffffff',
  bg:          '#f8f9fd',   // main content bg
  border:      'rgba(255,255,255,0.1)',
  text:        '#191c1f',
  textSec:     'rgba(255,255,255,0.6)',
  textMuted:   'rgba(255,255,255,0.35)',
  red:         '#ef4444',
  blue:        '#3b82f6',
  sidebarW:    260,
}

/* ── NavItem ─────────────────────────────────────────────── */
function NavItem({ to, icon, label, active }) {
  const [hov, setHov] = useState(false)
  return (
    <Link
      to={to}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '10px 16px',
        margin: '1px 0',
        borderRadius: 8,
        textDecoration: 'none',
        fontSize: 13.5,
        fontWeight: active ? 600 : 500,
        color: active ? '#ffffff' : C.textSec,
        background: active ? C.purpleBg : hov ? 'rgba(255,255,255,0.07)' : 'transparent',
        borderLeft: `3px solid ${active ? C.purple : 'transparent'}`,
        marginLeft: active ? 0 : 3,
        transition: 'background 0.15s, color 0.15s',
        lineHeight: 1,
      }}
    >
      <i
        className={`uil ${icon}`}
        style={{
          fontSize: 18,
          width: 22,
          textAlign: 'center',
          color: active ? '#ffffff' : 'rgba(255,255,255,0.55)',
          flexShrink: 0,
        }}
      />
      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {label}
      </span>
    </Link>
  )
}

/* ── Section label ───────────────────────────────────────── */
function SectionLabel({ label }) {
  return (
    <div style={{
      padding: '16px 16px 6px',
      fontSize: 9,
      fontWeight: 700,
      textTransform: 'uppercase',
      letterSpacing: 2.5,
      color: C.textMuted,
    }}>
      {label}
    </div>
  )
}

/* ── Icon badge (bell / envelope) ────────────────────────── */
function IconBtn({ icon, badgeCount, badgeColor }) {
  return (
    <button style={{
      position: 'relative', background: 'none', border: 'none',
      cursor: 'pointer', padding: '8px 10px', borderRadius: 10,
      color: C.text, display: 'flex', alignItems: 'center',
    }}>
      <i className={`uil ${icon}`} style={{ fontSize: 22 }} />
      {badgeCount > 0 && (
        <span style={{
          position: 'absolute', top: 4, right: 4,
          background: badgeColor, color: '#fff',
          width: 17, height: 17, borderRadius: '50%',
          fontSize: 9, fontWeight: 700,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          pointerEvents: 'none',
        }}>{badgeCount}</span>
      )}
    </button>
  )
}

/* ── Main layout ─────────────────────────────────────────── */
export default function GeexLayout() {
  const { user: clerkUser }  = useUser()
  const { signOut }          = useClerk()
  const { user: storeUser, logout } = useAuthStore()
  const location = useLocation()
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [isMobile, setIsMobile]       = useState(window.innerWidth < 900)
  const sidebarRef = useRef(null)

  const displayName = clerkUser?.fullName || clerkUser?.firstName || storeUser?.name || 'User'
  const userRole    = storeUser?.role || 'patient'
  const avatarUrl   = clerkUser?.imageUrl || null
  const initial     = displayName.charAt(0).toUpperCase()

  const roleLabel = userRole === 'admin' ? 'ADMIN'
                  : userRole === 'doctor' ? 'DOCTOR' : 'PATIENT'

  const handleLogout = async () => {
    logout()
    await signOut({ redirectUrl: '/login' })
  }

  const is = (path) => location.pathname === path

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 900)
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])

  // close sidebar when clicking outside (mobile)
  useEffect(() => {
    const handle = (e) => {
      if (sidebarOpen && sidebarRef.current && !sidebarRef.current.contains(e.target))
        setSidebarOpen(false)
    }
    document.addEventListener('mousedown', handle)
    return () => document.removeEventListener('mousedown', handle)
  }, [sidebarOpen])

  // auto-close sidebar on route change (mobile)
  useEffect(() => {
    if (isMobile) setSidebarOpen(false)
  }, [location.pathname])

  const showSidebar = !isMobile || sidebarOpen

  return (
    <div style={{
      display: 'flex',
      minHeight: '100vh',
      background: C.bg,
      fontFamily: "'Inter','Segoe UI',Arial,sans-serif",
    }}>

      {/* ── Mobile overlay ── */}
      {isMobile && sidebarOpen && (
        <div
          onClick={() => setSidebarOpen(false)}
          style={{
            position: 'fixed', inset: 0,
            background: 'rgba(0,0,0,0.4)',
            zIndex: 40,
          }}
        />
      )}

      {/* ════════════════ SIDEBAR ════════════════ */}
      <aside
        ref={sidebarRef}
        style={{
          width: C.sidebarW,
          background: C.sidebarBg,
          borderRight: 'none',
          position: 'fixed',
          top: 0, bottom: 0, left: 0,
          zIndex: 50,
          display: 'flex',
          flexDirection: 'column',
          transform: showSidebar ? 'translateX(0)' : `translateX(-${C.sidebarW}px)`,
          transition: 'transform 0.25s ease',
          overflowX: 'hidden',
          boxShadow: '4px 0 24px rgba(34,0,93,0.18)',
        }}
      >
        {/* Logo */}
        <div style={{
          height: 72,
          display: 'flex', alignItems: 'center', gap: 12,
          padding: '0 24px',
          borderBottom: `1px solid rgba(255,255,255,0.08)`,
          flexShrink: 0,
          marginBottom: 8,
        }}>
          <div style={{
            width: 40, height: 40, borderRadius: 12, flexShrink: 0,
            background: C.purple,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 2px 12px rgba(98,0,238,0.5)',
          }}>
            <i className="uil uil-stethoscope" style={{ color: '#fff', fontSize: 20 }} />
          </div>
          <div style={{ lineHeight: 1.25 }}>
            <div style={{ fontWeight: 800, fontSize: 15, color: '#ffffff', fontFamily: "'Manrope',sans-serif" }}>InstantDr</div>
            <div style={{ fontSize: 9, color: '#cfbdff', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 3 }}>Consult</div>
          </div>
        </div>

        {/* Navigation */}
        <nav style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', padding: '4px 12px 20px', scrollbarWidth: 'thin', scrollbarColor: '#3d1080 #22005d' }}>
        <style>{`
          aside nav::-webkit-scrollbar { width: 4px; }
          aside nav::-webkit-scrollbar-track { background: #22005d; }
          aside nav::-webkit-scrollbar-thumb { background: #3d1080; border-radius: 4px; }
        `}</style>

          <NavItem to="/dashboard" icon="uil-estate" label="Dashboard" active={is('/dashboard')} />

          {/* ── PATIENT & ALL USERS: AI Tools ── */}
          <SectionLabel label="AI Tools" />
          <NavItem to="/my-consult-openai" icon="uil-robot"       label="General Medicine AI"   active={is('/my-consult-openai')} />
          <NavItem to="/my-consult"        icon="uil-medkit"      label="Infectious Disease AI" active={is('/my-consult')} />
          <NavItem to="/ask-dr-sam"        icon="uil-comment-alt" label="Ask Dr Sam"            active={is('/ask-dr-sam')} />
          <NavItem to="/ask-dr-sam-chat"   icon="uil-brain"       label="Dr Sam Chat Consult"   active={is('/ask-dr-sam-chat')} />
          <NavItem to="/my-voice-agent"    icon="uil-microphone"  label="Voice Agent"           active={is('/my-voice-agent')} />

          {/* ── PATIENT (user role) section ── */}
          {(userRole === 'user' || userRole === 'admin') && (
            <>
              <SectionLabel label="My Health" />
              <NavItem to="/patient-profile"  icon="uil-user-md"        label="Health Profile"     active={is('/patient-profile')} />
              <NavItem to="/book-appointment" icon="uil-calendar-alt"   label="Book Appointment"   active={is('/book-appointment')} />
              <NavItem to="/my-appointments"  icon="uil-calendar-check" label="My Appointments"    active={is('/my-appointments')} />
              <NavItem to="/letters"          icon="uil-file-alt"       label="Medical Letters"    active={is('/letters')} />
              <NavItem to="/video-chat"       icon="uil-video"          label="Video Consult"      active={is('/video-chat')} />
            </>
          )}

          {/* ── DOCTOR section ── */}
          {(userRole === 'doctor') && (
            <>
              <SectionLabel label="Doctor" />
              <NavItem to="/doctor-setup"     icon="uil-stethoscope"    label="My Profile & Setup" active={is('/doctor-setup')} />
              <NavItem to="/my-appointments"  icon="uil-calendar-check" label="My Appointments"    active={is('/my-appointments')} />
              <NavItem to="/video-chat"       icon="uil-video"          label="Video Consult"      active={is('/video-chat')} />
              <NavItem to="/letters"          icon="uil-file-alt"       label="Medical Letters"    active={is('/letters')} />
            </>
          )}

          {/* ── MCQ available to all ── */}
          <SectionLabel label="Education" />
          <NavItem to="/mcq-test" icon="uil-question-circle" label="MCQ Test" active={is('/mcq-test')} />

          <SectionLabel label="Account" />
          <NavItem to="/profile" icon="uil-user-circle" label="My Profile" active={is('/profile')} />

          {/* ── ADMIN only ── */}
          {userRole === 'admin' && (
            <>
              <SectionLabel label="Administrator" />
              <NavItem to="/admin"        icon="uil-shield"      label="Admin Panel"       active={is('/admin')} />
              <NavItem to="/doctor-setup" icon="uil-stethoscope" label="Doctor Setup"      active={is('/doctor-setup')} />
            </>
          )}
        </nav>

        {/* Sidebar footer */}
        <div style={{
          padding: '12px 16px',
          borderTop: `1px solid rgba(255,255,255,0.1)`,
          fontSize: 11, color: 'rgba(255,255,255,0.35)',
          textAlign: 'center', flexShrink: 0,
        }}>
          © {new Date().getFullYear()} InstantDrConsult
        </div>
      </aside>

      {/* ════════════════ MAIN CONTENT ════════════════ */}
      <div style={{
        marginLeft: isMobile ? 0 : C.sidebarW,
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        minWidth: 0,
        minHeight: '100vh',
      }}>

        {/* ── TOP HEADER ── */}
        <header style={{
          height: 64,
          background: 'rgba(255,255,255,0.88)',
          backdropFilter: 'blur(20px)',
          borderBottom: `1px solid rgba(203,195,217,0.25)`,
          display: 'flex',
          alignItems: 'center',
          padding: '0 20px',
          gap: 12,
          position: 'sticky',
          top: 0, zIndex: 30,
          flexShrink: 0,
        }}>
          {/* Hamburger */}
          <button
            onClick={() => setSidebarOpen(s => !s)}
            style={{
              background: 'none', border: 'none', cursor: 'pointer',
              padding: 8, borderRadius: 8,
              color: '#6b7280', display: 'flex', alignItems: 'center',
              flexShrink: 0,
            }}
          >
            <i className="uil uil-bars" style={{ fontSize: 22 }} />
          </button>

          {/* Search bar */}
          <div style={{
            flex: 1, maxWidth: 460,
            display: 'flex', alignItems: 'center', gap: 8,
            background: '#e7e8ec',
            border: 'none',
            borderRadius: 24, padding: '0 16px', height: 38,
          }}>
            <i className="uil uil-search" style={{ color: '#7a7488', fontSize: 16, flexShrink: 0 }} />
            <input
              placeholder="Search..."
              style={{
                border: 'none', background: 'none', outline: 'none',
                fontSize: 13, color: C.text, width: '100%', height: '100%',
              }}
            />
          </div>

          <div style={{ flex: 1 }} />

          {/* Bell + Envelope */}
          <IconBtn icon="uil-bell"     badgeCount={3} badgeColor={C.red}  />
          <IconBtn icon="uil-envelope" badgeCount={5} badgeColor={C.blue} />

          {/* Divider */}
          <div style={{ width: 1, height: 32, background: '#e5e7eb', margin: '0 4px' }} />

          {/* User info + avatar + logout */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ textAlign: 'right', lineHeight: 1.3 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: C.text }}>{displayName}</div>
              <div style={{ fontSize: 10, color: '#7a7488', fontWeight: 600, letterSpacing: 0.5, textTransform: 'uppercase' }}>
                {roleLabel}
              </div>
            </div>

            {avatarUrl ? (
              <img
                src={avatarUrl}
                alt={displayName}
                style={{
                  width: 36, height: 36, borderRadius: '50%',
                  objectFit: 'cover',
                  border: `2px solid #e8ddff`,
                }}
                onError={e => { e.currentTarget.style.display = 'none' }}
              />
            ) : (
              <div style={{
                width: 36, height: 36, borderRadius: '50%', flexShrink: 0,
                background: `linear-gradient(135deg, ${C.purple}, ${C.purpleDark})`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: '#fff', fontWeight: 700, fontSize: 14,
              }}>{initial}</div>
            )}

            <button
              onClick={handleLogout}
              style={{
                display: 'flex', alignItems: 'center', gap: 5,
                background: 'none', border: 'none',
                cursor: 'pointer', padding: '6px 2px',
                fontSize: 13, color: '#6b7280', fontWeight: 500,
              }}
            >
              <i className="uil uil-sign-out-alt" style={{ fontSize: 17 }} />
              Logout
            </button>
          </div>
        </header>

        {/* ── PAGE CONTENT ── */}
        <main style={{ flex: 1, padding: '24px 24px', overflowY: 'auto' }}>
          <Outlet />
        </main>
      </div>
    </div>
  )
}
